import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { CheckoutFormData, Course } from '../types';
import PricingTable from '../components/PricingTable';
import StripeCheckoutForm from '../components/StripeCheckoutForm';
import { usePageTitle } from '../hooks/usePageTitle';

declare global {
  interface Window {
    turnstile?: {
      render: (selector: string, options: Record<string, unknown>) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

const steps = ['Tier Selection', 'Contact & Billing', 'Student Info', 'Payment'];

const initialFormData: CheckoutFormData = {
  tier: 'Starter',
  selectedCourses: [],
  discountCode: '',
  discountApplied: false,
  discountAmount: 0,
  contact: {
    firstName: '',
    lastName: '',
    company: '',
    email: '',
    phone: '',
    jobTitle: '',
    address: '',
    suite: '',
    city: '',
    state: '',
    postalCode: '',
  },
  students: [{ firstName: '', lastName: '', email: '' }],
  paymentMethod: 'stripe',
  poNumber: '',
  prepaidDetails: '',
};

const tierPrices: Record<string, number> = {
  Starter: 999,
  Pro: 2499,
  Team: 4999,
};

const Checkout: React.FC = () => {
  usePageTitle('Checkout');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<CheckoutFormData>(initialFormData);
  const [courses, setCourses] = useState<Course[]>([]);
  const [discountValidating, setDiscountValidating] = useState(false);
  const [discountError, setDiscountError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [showStripeCheckout, setShowStripeCheckout] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [eulaAccepted, setEulaAccepted] = useState(false);
  const turnstileWidgetIdRef = useRef<string | null>(null);

  useEffect(() => {
    const tier = searchParams.get('tier');
    if (tier && (tier === 'Starter' || tier === 'Pro' || tier === 'Team')) {
      setFormData((prev) => ({ ...prev, tier }));
    }
    const courseId = searchParams.get('courseId');
    if (courseId) {
      setFormData((prev) => ({ ...prev, selectedCourses: [courseId] }));
    }

    fetch('/api/courses')
      .then((res) => res.json())
      .then((data: Course[]) => setCourses(data.filter((c) => c.active)))
      .catch(console.error);
  }, [searchParams]);

  useEffect(() => {
    if (user) {
      setFormData((prev) => ({
        ...prev,
        contact: {
          ...prev.contact,
          firstName: user.name?.split(' ')[0] || '',
          lastName: user.name?.split(' ').slice(1).join(' ') || '',
          email: user.email || '',
        },
        students: [
          {
            firstName: user.name?.split(' ')[0] || '',
            lastName: user.name?.split(' ').slice(1).join(' ') || '',
            email: user.email || '',
          },
        ],
      }));
    }
  }, [user]);


  // Load Cloudflare Turnstile on payment step
  useEffect(() => {
    if (currentStep !== 3) return;

    const renderWidget = () => {
      if (window.turnstile && !turnstileWidgetIdRef.current) {
        const el = document.getElementById('turnstile-widget');
        if (el) {
          turnstileWidgetIdRef.current = window.turnstile.render('#turnstile-widget', {
            sitekey: process.env.REACT_APP_TURNSTILE_SITE_KEY || '0x4AAAAAACt9o10V71M2oOXh',
            callback: (token: string) => setTurnstileToken(token),
            'error-callback': () => {
              console.warn('Turnstile widget error — proceeding without CAPTCHA');
              setTurnstileToken('');
            },
            'timeout-callback': () => {
              console.warn('Turnstile widget timed out — proceeding without CAPTCHA');
              setTurnstileToken('');
            },
          });
        }
      }
    };

    if (window.turnstile) {
      renderWidget();
    } else if (!document.getElementById('turnstile-script')) {
      const script = document.createElement('script');
      script.id = 'turnstile-script';
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      script.async = true;
      script.defer = true;
      script.onload = renderWidget;
      script.onerror = () => {
        console.warn('Failed to load Turnstile script — proceeding without CAPTCHA');
      };
      document.head.appendChild(script);
    } else {
      // Script tag exists but hasn't loaded yet — poll briefly (max 5s)
      let attempts = 0;
      const interval = setInterval(() => {
        attempts++;
        if (window.turnstile) {
          clearInterval(interval);
          renderWidget();
        } else if (attempts >= 25) {
          clearInterval(interval);
          console.warn('Turnstile script never loaded — proceeding without CAPTCHA');
        }
      }, 200);
      return () => clearInterval(interval);
    }

    return () => {
      if (turnstileWidgetIdRef.current && window.turnstile) {
        window.turnstile.remove(turnstileWidgetIdRef.current);
        turnstileWidgetIdRef.current = null;
      }
    };
  }, [currentStep]);

  const handleTierSelect = (tier: 'Starter' | 'Pro' | 'Team') => {
    const maxStudents = tier === 'Team' ? 10 : 1;
    const currentStudents = formData.students;
    const students =
      currentStudents.length > maxStudents
        ? currentStudents.slice(0, maxStudents)
        : currentStudents;
    setFormData((prev) => ({ ...prev, tier, students }));
  };

  const handleContactChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      contact: { ...prev.contact, [field]: value },
    }));
  };

  const handleStudentChange = (index: number, field: string, value: string) => {
    const updated = [...formData.students];
    updated[index] = { ...updated[index], [field]: value };
    setFormData((prev) => ({ ...prev, students: updated }));
  };

  const addStudent = () => {
    const maxStudents = formData.tier === 'Team' ? 10 : 1;
    if (formData.students.length < maxStudents) {
      setFormData((prev) => ({
        ...prev,
        students: [...prev.students, { firstName: '', lastName: '', email: '' }],
      }));
    }
  };

  const removeStudent = (index: number) => {
    if (formData.students.length > 1) {
      const updated = formData.students.filter((_, i) => i !== index);
      setFormData((prev) => ({ ...prev, students: updated }));
    }
  };

  const handleCourseToggle = (courseId: string) => {
    const maxCourses = formData.tier === 'Starter' ? 1 : courses.length || 9;
    setFormData((prev) => {
      const selected = prev.selectedCourses.includes(courseId)
        ? prev.selectedCourses.filter((id) => id !== courseId)
        : prev.selectedCourses.length < maxCourses
        ? [...prev.selectedCourses, courseId]
        : prev.selectedCourses;
      return { ...prev, selectedCourses: selected };
    });
  };

  const validateDiscount = async () => {
    if (!formData.discountCode.trim()) return;
    setDiscountValidating(true);
    setDiscountError('');
    try {
      const res = await fetch('/api/discounts/validate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: formData.discountCode, tier: formData.tier }),
      });
      if (res.ok) {
        const data = await res.json();
        setFormData((prev) => ({
          ...prev,
          discountApplied: true,
          discountAmount: data.discountAmount || 0,
        }));
      } else {
        const error = await res.json();
        setDiscountError(error.message || 'Invalid discount code');
        setFormData((prev) => ({ ...prev, discountApplied: false, discountAmount: 0 }));
      }
    } catch {
      setDiscountError('Error validating discount code');
    } finally {
      setDiscountValidating(false);
    }
  };

  const getTotal = () => {
    const base = tierPrices[formData.tier] || 0;
    return base - formData.discountAmount;
  };

  const canProceed = (): boolean => {
    switch (currentStep) {
      case 0:
        return formData.selectedCourses.length > 0;
      case 1:
        return !!(
          formData.contact.firstName &&
          formData.contact.lastName &&
          formData.contact.email &&
          formData.contact.company &&
          formData.contact.phone
        );
      case 2: {
        // For Team tier, only the first student (coordinator) is required;
        // additional students can be added later in Team Setup.
        const firstStudent = formData.students[0];
        if (!firstStudent || !firstStudent.firstName || !firstStudent.lastName || !firstStudent.email) return false;
        if (formData.tier === 'Team') return true;
        return formData.students.every((s) => s.firstName && s.lastName && s.email);
      }
      case 3:
        if (!eulaAccepted) return false;
        if (formData.paymentMethod === 'purchase_order') return !!formData.poNumber;
        if (formData.paymentMethod === 'prepaid') return !!formData.prepaidDetails;
        return true;
      default:
        return false;
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError('');

    // Filter out incomplete student entries (Team tier allows partial roster)
    const completeStudents = formData.students.filter(
      (s) => s.firstName && s.lastName && s.email
    );
    const submissionData = { ...formData, students: completeStudents };

    try {
      if (formData.paymentMethod === 'credit_card') {
        // Calculate amounts in cents for API
        const basePrice = tierPrices[formData.tier];
        const totalAmount = getTotal();
        const discountAmount = formData.discountApplied ? basePrice - totalAmount : 0;

        // Call the payment API to create invoice
        const res = await fetch('/api/payments/create-invoice', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...submissionData,
            totalAmount: Math.round(totalAmount * 100), // Convert to cents
            discountAmount: Math.round(discountAmount * 100),
            turnstileToken: turnstileToken || undefined,
          }),
        });

        if (res.ok) {
          const data = await res.json();
          // Store pending subscription info for post-payment detection
          localStorage.setItem('pendingSubscription', JSON.stringify({
            subscriptionId: data.subscriptionId,
            tier: formData.tier,
          }));
          // Redirect to QuickBooks payment page
          window.location.href = data.paymentUrl;
        } else {
          const error = await res.json();
          setSubmitError(error.error || error.message || 'Failed to create payment. Please try again.');
        }
        return;
      }

      // PO, prepaid, and any other payment method — call /api/checkout
      const res = await fetch('/api/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...submissionData, turnstileToken: turnstileToken || undefined }),
      });

      if (res.ok) {
        const data = await res.json();
        const subId = data.subscriptionId;

        if (formData.paymentMethod === 'purchase_order' || formData.paymentMethod === 'prepaid') {
          // PO and prepaid subscriptions need admin/sales verification before activation.
          // Route to confirmation page that explains the 1-2 business day approval process.
          navigate('/checkout/confirmation', {
            state: {
              orderData: formData,
              paymentMethod: formData.paymentMethod,
              total: getTotal(),
              subscriptionId: subId,
            },
          });
        } else {
          navigate('/dashboard?checkout=success');
        }
      } else {
        const error = await res.json();
        setSubmitError(error.message || 'Checkout failed. Please try again.');
      }
    } catch {
      setSubmitError('An error occurred. Please try again.');
    } finally {
      setSubmitting(false);
      // Reset Turnstile widget for retry — tokens are single-use
      if (turnstileWidgetIdRef.current && window.turnstile) {
        window.turnstile.reset(turnstileWidgetIdRef.current);
        setTurnstileToken('');
      }
    }
  };

  const maxCourses = formData.tier === 'Starter' ? 1 : courses.length || 9;

  return (
    <div className="checkout-page">
      <section className="checkout-header">
        <div className="section-container">
          <h1 className="page-title">Checkout</h1>
          <div className="checkout-steps">
            {steps.map((step, index) => (
              <div
                key={step}
                className={`checkout-step ${index === currentStep ? 'step-active' : ''} ${
                  index < currentStep ? 'step-completed' : ''
                }`}
              >
                <span className="step-number">{index < currentStep ? '\u2713' : index + 1}</span>
                <span className="step-label">{step}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="section-container">
          <div className="checkout-content">
            {/* Step 1: Tier Selection */}
            {currentStep === 0 && (
              <div className="checkout-step-content">
                <h2>Select Your Plan</h2>
                <PricingTable onSelectTier={handleTierSelect} selectedTier={formData.tier} compact />

                <div className="course-selection">
                  <h3>Select Your Courses {formData.tier === 'Starter' ? '(1 course)' : '(unlimited)'}</h3>
                  <p className="selection-hint">
                    {formData.selectedCourses.length} of {formData.tier === 'Starter' ? '1' : courses.length} selected
                  </p>
                  <div className="course-selection-grid">
                    {courses.map((course) => (
                      <label
                        key={course.id}
                        className={`course-selection-item ${
                          formData.selectedCourses.includes(course.id) ? 'course-selected' : ''
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={formData.selectedCourses.includes(course.id)}
                          onChange={() => handleCourseToggle(course.id)}
                          disabled={
                            !formData.selectedCourses.includes(course.id) &&
                            formData.selectedCourses.length >= maxCourses
                          }
                        />
                        <div className="course-selection-info">
                          <span className="course-selection-title">{course.title}</span>
                          <span className="course-selection-track">{course.track}</span>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="discount-section">
                  <h3>Discount Code</h3>
                  <div className="discount-row">
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Enter discount code"
                      value={formData.discountCode}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          discountCode: e.target.value,
                          discountApplied: false,
                          discountAmount: 0,
                        }))
                      }
                    />
                    <button
                      className="btn btn-outline"
                      onClick={validateDiscount}
                      disabled={discountValidating || !formData.discountCode.trim()}
                    >
                      {discountValidating ? 'Validating...' : 'Apply'}
                    </button>
                  </div>
                  {discountError && <p className="form-error">{discountError}</p>}
                  {formData.discountApplied && (
                    <p className="form-success">
                      Discount applied! You save ${formData.discountAmount.toFixed(2)}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Step 2: Contact & Billing */}
            {currentStep === 1 && (
              <div className="checkout-step-content">
                <h2>Contact &amp; Billing Information</h2>
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">First Name *</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.contact.firstName}
                      onChange={(e) => handleContactChange('firstName', e.target.value)}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Last Name *</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.contact.lastName}
                      onChange={(e) => handleContactChange('lastName', e.target.value)}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Company *</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.contact.company}
                      onChange={(e) => handleContactChange('company', e.target.value)}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Job Title</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.contact.jobTitle}
                      onChange={(e) => handleContactChange('jobTitle', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Email *</label>
                    <input
                      type="email"
                      className="form-input"
                      value={formData.contact.email}
                      onChange={(e) => handleContactChange('email', e.target.value)}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Phone *</label>
                    <input
                      type="tel"
                      className="form-input"
                      value={formData.contact.phone}
                      onChange={(e) => handleContactChange('phone', e.target.value)}
                      required
                    />
                  </div>
                  <div className="form-group form-full">
                    <label className="form-label">Address</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.contact.address}
                      onChange={(e) => handleContactChange('address', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Suite/Unit</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.contact.suite}
                      onChange={(e) => handleContactChange('suite', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">City</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.contact.city}
                      onChange={(e) => handleContactChange('city', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">State</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.contact.state}
                      onChange={(e) => handleContactChange('state', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Postal Code</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.contact.postalCode}
                      onChange={(e) => handleContactChange('postalCode', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Student Info */}
            {currentStep === 2 && (
              <div className="checkout-step-content">
                <h2>Student Information</h2>
                <p className="step-description">
                  {formData.tier === 'Team'
                    ? `Add up to 10 students for your Team plan.`
                    : `Enter student details for enrollment.`}
                </p>
                {formData.students.map((student, index) => (
                  <div key={index} className="student-row">
                    <div className="student-row-header">
                      <h4>Student {index + 1}</h4>
                      {formData.students.length > 1 && (
                        <button
                          className="btn btn-text btn-danger"
                          onClick={() => removeStudent(index)}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                    <div className="form-grid">
                      <div className="form-group">
                        <label className="form-label">First Name *</label>
                        <input
                          type="text"
                          className="form-input"
                          value={student.firstName}
                          onChange={(e) =>
                            handleStudentChange(index, 'firstName', e.target.value)
                          }
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Last Name *</label>
                        <input
                          type="text"
                          className="form-input"
                          value={student.lastName}
                          onChange={(e) =>
                            handleStudentChange(index, 'lastName', e.target.value)
                          }
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Email *</label>
                        <input
                          type="email"
                          className="form-input"
                          value={student.email}
                          onChange={(e) =>
                            handleStudentChange(index, 'email', e.target.value)
                          }
                          required
                        />
                      </div>
                    </div>
                  </div>
                ))}
                {formData.tier === 'Team' && formData.students.length < 10 && (
                  <button className="btn btn-outline add-student-btn" onClick={addStudent}>
                    + Add Another Student
                  </button>
                )}
              </div>
            )}

            {/* Step 4: Payment */}
            {currentStep === 3 && (
              <div className="checkout-step-content">
                <h2>Payment</h2>

                <div className="order-summary">
                  <h3>Order Summary</h3>
                  <div className="summary-row">
                    <span>Plan</span>
                    <span>{formData.tier}</span>
                  </div>
                  <div className="summary-row">
                    <span>Courses</span>
                    <span>{formData.selectedCourses.length} selected</span>
                  </div>
                  <div className="summary-row">
                    <span>Students</span>
                    <span>{formData.students.length}</span>
                  </div>
                  <div className="summary-row">
                    <span>Subtotal</span>
                    <span>${tierPrices[formData.tier].toLocaleString()}</span>
                  </div>
                  {formData.discountApplied && (
                    <div className="summary-row summary-discount">
                      <span>Discount ({formData.discountCode})</span>
                      <span>-${formData.discountAmount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="summary-row summary-total">
                    <span>Total</span>
                    <span>${getTotal().toLocaleString()}</span>
                  </div>
                </div>

                <div className="payment-methods">
                  <h3>Payment Method</h3>
                  <div className="payment-options">
                    <label
                      className={`payment-option ${
                        formData.paymentMethod === 'stripe' ? 'payment-selected' : ''
                      }`}
                    >
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="stripe"
                        checked={formData.paymentMethod === 'stripe'}
                        onChange={() => {
                          setFormData((prev) => ({ ...prev, paymentMethod: 'stripe' }));
                          setShowStripeCheckout(false);
                        }}
                      />
                      <div className="payment-option-content">
                        <span className="payment-option-title">Credit Card</span>
                        <span className="payment-option-description">
                          Pay securely with your credit or debit card. Your subscription will be activated immediately.
                        </span>
                      </div>
                    </label>
                    <label
                      className={`payment-option ${
                        formData.paymentMethod === 'credit_card' ? 'payment-selected' : ''
                      }`}
                    >
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="credit_card"
                        checked={formData.paymentMethod === 'credit_card'}
                        onChange={() => {
                          setFormData((prev) => ({ ...prev, paymentMethod: 'credit_card' }));
                          setShowStripeCheckout(false);
                        }}
                      />
                      <div className="payment-option-content">
                        <span className="payment-option-title">Invoice</span>
                        <span className="payment-option-description">
                          Pay now or later via ACH, Credit Card, or Apple Pay. Your subscription will become active within 1 to 2 business days after payment has been verified.
                        </span>
                      </div>
                    </label>
                    <label
                      className={`payment-option ${
                        formData.paymentMethod === 'purchase_order' ? 'payment-selected' : ''
                      }`}
                    >
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="purchase_order"
                        checked={formData.paymentMethod === 'purchase_order'}
                        onChange={() => {
                          setFormData((prev) => ({ ...prev, paymentMethod: 'purchase_order' }));
                          setShowStripeCheckout(false);
                        }}
                      />
                      <div className="payment-option-content">
                        <span className="payment-option-title">Purchase Order</span>
                        <span className="payment-option-description">
                          Submit a purchase order number. Net 30 payment terms.
                        </span>
                      </div>
                    </label>
                    <label
                      className={`payment-option ${
                        formData.paymentMethod === 'prepaid' ? 'payment-selected' : ''
                      }`}
                    >
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="prepaid"
                        checked={formData.paymentMethod === 'prepaid'}
                        onChange={() => {
                          setFormData((prev) => ({ ...prev, paymentMethod: 'prepaid' }));
                          setShowStripeCheckout(false);
                        }}
                      />
                      <div className="payment-option-content">
                        <span className="payment-option-title">Prepaid or Voucher</span>
                        <span className="payment-option-description">
                          Enter the Patriot Account Executive name and/or Voucher Number.
                        </span>
                      </div>
                    </label>
                  </div>

                  {formData.paymentMethod === 'purchase_order' && (
                    <div className="form-group payment-detail">
                      <label className="form-label">Purchase Order Number *</label>
                      <input
                        type="text"
                        className="form-input"
                        value={formData.poNumber}
                        onChange={(e) =>
                          setFormData((prev) => ({ ...prev, poNumber: e.target.value }))
                        }
                        placeholder="Enter PO number"
                        required
                      />
                    </div>
                  )}

                  {formData.paymentMethod === 'prepaid' && (
                    <div className="form-group payment-detail">
                      <label className="form-label">Prepaid or Voucher *</label>
                      <input
                        type="text"
                        className="form-input"
                        value={formData.prepaidDetails}
                        onChange={(e) =>
                          setFormData((prev) => ({ ...prev, prepaidDetails: e.target.value }))
                        }
                        placeholder="Enter Voucher # or Patriot Account Executive Name"
                        required
                      />
                    </div>
                  )}
                </div>

                {/* Turnstile CAPTCHA Widget */}
                <div className="form-group payment-detail">
                  <div id="turnstile-widget"></div>
                </div>

                {/* EULA Agreement Checkbox */}
                <div className="form-group eula-group">
                  <label className={`eula-checkbox${!eulaAccepted ? ' eula-unchecked' : ''}`}>
                    <input
                      type="checkbox"
                      checked={eulaAccepted}
                      onChange={(e) => setEulaAccepted(e.target.checked)}
                    />
                    <span>I agree to the <a href="/terms" target="_blank" rel="noopener noreferrer">terms</a> of Patriot Academy Premium and the <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.{!eulaAccepted && <span className="eula-required"> *</span>}</span>
                  </label>
                </div>

                {/* Stripe Embedded Checkout */}
                {formData.paymentMethod === 'stripe' && showStripeCheckout && (
                  <StripeCheckoutForm
                    formData={formData}
                    totalAmount={getTotal()}
                    discountAmount={formData.discountApplied ? formData.discountAmount : 0}
                    onError={(msg) => setSubmitError(msg)}
                    turnstileToken={turnstileToken || undefined}
                  />
                )}

                {submitError && <div className="form-error checkout-error">{submitError}</div>}
              </div>
            )}

            {/* Navigation */}
            <div className={`checkout-nav${currentStep === steps.length - 1 ? ' checkout-nav-reversed' : ''}`}>
              {currentStep > 0 && currentStep < steps.length - 1 && (
                <button
                  className="btn btn-outline"
                  onClick={() => setCurrentStep((prev) => prev - 1)}
                >
                  Back
                </button>
              )}
              <div className="checkout-nav-right">
                {currentStep < steps.length - 1 ? (
                  <button
                    className="btn btn-primary"
                    onClick={() => setCurrentStep((prev) => prev + 1)}
                    disabled={!canProceed()}
                  >
                    Continue
                  </button>
                ) : formData.paymentMethod === 'stripe' ? (
                  !showStripeCheckout ? (
                    <button
                      className="btn btn-primary btn-lg"
                      onClick={() => {
                        setSubmitError('');
                        setShowStripeCheckout(true);
                      }}
                      disabled={!canProceed()}
                    >
                      Pay with Credit Card
                    </button>
                  ) : null
                ) : (
                  <button
                    className="btn btn-primary btn-lg"
                    onClick={handleSubmit}
                    disabled={!canProceed() || submitting}
                  >
                    {submitting
                      ? 'Processing...'
                      : formData.paymentMethod === 'credit_card'
                      ? 'Proceed to Payment'
                      : 'Complete Order'}
                  </button>
                )}
              </div>
              {currentStep === steps.length - 1 && (
                <button
                  className="btn btn-outline"
                  onClick={() => setCurrentStep((prev) => prev - 1)}
                >
                  Back
                </button>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Checkout;
